import React, { useContext } from "react";

// Third-party
import PropTypes from "prop-types";

const DispatchContext = React.createContext();
const StateContext = React.createContext();

/**
 * Initial state for the Results Context
 */
const initialState = {
  generatedResistanceMaps: null,
  generatedCorridors: null,
  generatedComparisonMaps: null,
  generatedPaths: null,
  generatedSites: null,
  resultsMode: "all",
  // Number of generations currently being generated per result type. The derived
  // `isGenerating*` booleans below are true while their counter is > 0, so
  // concurrent generations (e.g. generating several scenarios at once) keep
  // the loader spinning until the last one finishes.
  generatingResistanceMapCount: 0,
  generatingCorridorCount: 0,
  generatingComparisonMapCount: 0,
  generatingPathCount: 0,
  generatingSitesCount: 0,
  isGeneratingResistanceMap: false,
  isGeneratingCorridor: false,
  isGeneratingComparisonMap: false,
  isGeneratingPath: false,
  isGeneratingSites: false
};

/**
 * Reducer with the actions to update the Results Context state.
 * @param { object } state
 * @param { object } action
 * @returns
 */
/**
 * Applies an in-flight generation counter update for a given result type and
 * derives the matching boolean flag. A truthy payload increments the counter,
 * a falsy one decrements it (floored at 0). The `isGenerating*` flag is true
 * while the counter is greater than 0.
 *
 * @param { object } state
 * @param { string } countKey - e.g. "generatingResistanceMapCount"
 * @param { string } flagKey - e.g. "isGeneratingResistanceMap"
 * @param { boolean } value
 * @returns { object }
 */
function applyGeneratingCount(state, countKey, flagKey, value) {
  const count = Math.max(0, state[countKey] + (value ? 1 : -1));
  return {
    ...state,
    [countKey]: count,
    [flagKey]: count > 0
  };
}

function ResultsReducer(state, action) {
  switch (action.type) {
    case "GENERATED_ALL_SET": {
      return {
        ...state,
        generatedResistanceMaps: action.payload.scenarioId,
        generatedCorridors: action.payload.scenarioId,
        generatedComparisonMaps: action.payload.scenarioId,
        generatedPaths: action.payload.scenarioId,
        generatedSites: action.payload.scenarioId
      };
    }
    case "GENERATED_RESISTANCE_MAPS_CLEAR": {
      return {
        ...state,
        generatedResistanceMaps: null
      };
    }
    case "GENERATED_CORRIDORS_CLEAR": {
      return {
        ...state,
        generatedCorridors: null
      };
    }
    case "GENERATED_COMPARISON_MAPS_CLEAR": {
      return {
        ...state,
        generatedComparisonMaps: null
      };
    }
    case "GENERATED_PATHS_CLEAR": {
      return {
        ...state,
        generatedPaths: null
      };
    }

    case "GENERATED_SITES_CLEAR": {
      return {
        ...state,
        generatedSites: null
      };
    }

    case "RESULTS_MODE_SET": {
      return {
        ...state,
        resultsMode: action.payload.resultsMode
      };
    }

    case "IS_GENERATING_RESISTANCE_SET": {
      return applyGeneratingCount(
        state,
        "generatingResistanceMapCount",
        "isGeneratingResistanceMap",
        action.payload.isGeneratingResistanceMap
      );
    }

    case "IS_GENERATING_CORRIDOR_SET": {
      return applyGeneratingCount(
        state,
        "generatingCorridorCount",
        "isGeneratingCorridor",
        action.payload.isGeneratingCorridor
      );
    }

    case "IS_GENERATING_COMPARISON_MAP_SET": {
      return applyGeneratingCount(
        state,
        "generatingComparisonMapCount",
        "isGeneratingComparisonMap",
        action.payload.isGeneratingComparisonMap
      );
    }

    case "IS_GENERATING_PATH_SET": {
      return applyGeneratingCount(
        state,
        "generatingPathCount",
        "isGeneratingPath",
        action.payload.isGeneratingPath
      );
    }

    case "IS_GENERATING_SITES_SET": {
      return applyGeneratingCount(
        state,
        "generatingSitesCount",
        "isGeneratingSites",
        action.payload.isGeneratingSites
      );
    }
    default: {
      return state;
    }
  }
}

/**
 * Provides all children with the state and the dispatch of the
 * Results Context.
 *
 * @param { node } children
 * @returns node
 */
function ResultsProvider({ children }) {
  const [state, dispatch] = React.useReducer(ResultsReducer, initialState);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
}

ResultsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access and manage the state of the Results Context
 */
const useResults = () => {
  const resultsState = useContext(StateContext);
  const resultsDispatch = useContext(DispatchContext);

  // EFFECTS  //////////////////////////

  // PRIVATE METHODS   //////////////////////////

  // PUBLIC METHODS   //////////////////////////

  const setGeneratedAll = scenarioId => {
    resultsDispatch({
      type: "GENERATED_ALL_SET",
      payload: { scenarioId: scenarioId }
    });
  };

  const clearGeneratedAllResistanceMaps = () => {
    resultsDispatch({ type: "GENERATED_RESISTANCE_MAPS_CLEAR" });
  };

  const clearGeneratedAllCorridors = () => {
    resultsDispatch({ type: "GENERATED_CORRIDORS_CLEAR" });
  };

  const clearGeneratedAllComparisonMaps = () => {
    resultsDispatch({ type: "GENERATED_COMPARISON_MAPS_CLEAR" });
  };

  const clearGeneratedAllPaths = () => {
    resultsDispatch({ type: "GENERATED_PATHS_CLEAR" });
  };

  const clearGeneratedAllSites = () => {
    resultsDispatch({ type: "GENERATED_SITES_CLEAR" });
  };

  const setResultsMode = mode => {
    resultsDispatch({
      type: "RESULTS_MODE_SET",
      payload: { resultsMode: mode }
    });
  };

  const setIsGenerating = (item, value) => {
    if (item === "resistanceMap") {
      resultsDispatch({
        type: "IS_GENERATING_RESISTANCE_SET",
        payload: { isGeneratingResistanceMap: value }
      });
    } else if (item === "corridor") {
      resultsDispatch({
        type: "IS_GENERATING_CORRIDOR_SET",
        payload: { isGeneratingCorridor: value }
      });
    } else if (item === "comparisonMap") {
      resultsDispatch({
        type: "IS_GENERATING_COMPARISON_MAP_SET",
        payload: { isGeneratingComparisonMap: value }
      });
    } else if (item === "path") {
      resultsDispatch({
        type: "IS_GENERATING_PATH_SET",
        payload: { isGeneratingPath: value }
      });
    } else if (item === "sites") {
      resultsDispatch({
        type: "IS_GENERATING_SITES_SET",
        payload: { isGeneratingSites: value }
      });
    }
  };

  return [
    resultsState,
    {
      setGeneratedAll: setGeneratedAll,
      clearGeneratedAllResistanceMaps: clearGeneratedAllResistanceMaps,
      clearGeneratedAllCorridors: clearGeneratedAllCorridors,
      clearGeneratedAllComparisonMaps: clearGeneratedAllComparisonMaps,
      clearGeneratedAllPaths: clearGeneratedAllPaths,
      clearGeneratedAllSites: clearGeneratedAllSites,
      setResultsMode: setResultsMode,
      setIsGenerating: setIsGenerating
    }
  ];
};

export { useResults };

export default ResultsProvider;
